<template>
  <div class="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
    <div class="sm:mx-auto sm:w-full sm:max-w-md">
      <div class="text-center">
        <img
          class="mx-auto h-12 w-auto"
          src="/HYS-logo-icon-06.png"
          alt="北美好医生"
        />
        <h1 class="mt-6 text-3xl font-bold text-gray-900">
          {{ getErrorTitle() }}
        </h1>
        <p class="mt-2 text-sm text-gray-600">
          {{ getErrorMessage() }}
        </p>
      </div>
    </div>

    <div class="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
      <div class="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
        <div class="space-y-6">
          <!-- Development debug information -->
          <ClientOnly>
            <div v-if="isDevelopment && props.error" class="bg-red-50 border border-red-200 rounded-lg p-4">
              <h3 class="text-sm font-medium text-red-800 mb-2">开发调试信息</h3>
              <div class="text-xs text-red-700 space-y-2">
                <div><strong>错误码:</strong> {{ props.error.statusCode }}</div>
                <div><strong>错误信息:</strong> {{ props.error.statusMessage || props.error.message }}</div>
                <div v-if="props.error.data"><strong>错误数据:</strong> 
                  <pre class="mt-1 whitespace-pre-wrap">{{ JSON.stringify(props.error.data, null, 2) }}</pre>
                </div>
                <div v-if="props.error.stack"><strong>堆栈跟踪:</strong>
                  <pre class="mt-1 whitespace-pre-wrap text-xs bg-red-100 p-2 rounded overflow-x-auto">{{ props.error.stack }}</pre>
                </div>
              </div>
            </div>
          </ClientOnly>

          <!-- Action buttons -->
          <div class="flex flex-col space-y-4">
            <button
              @click="handleRefresh"
              class="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
            >
              刷新页面
            </button>
            
            <button
              @click="handleGoHome"
              class="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
            >
              返回首页
            </button>
          </div>

          <!-- Contact support -->
          <div class="text-center pt-4 border-t border-gray-200">
            <p class="text-sm text-gray-500">
              如果问题持续存在，请
              <a 
                href="/contact" 
                class="font-medium text-blue-600 hover:text-blue-500 transition-colors"
              >
                联系客服
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue'

// Enhanced error types for better TypeScript support
type NuxtError = {
  statusCode?: number
  statusMessage?: string
  message?: string
  stack?: string
  data?: Record<string, unknown>
}

// Props from Nuxt error handling
const props = defineProps<{
  error: NuxtError
}>()

// Development mode detection - safe for SSR
const isDevelopment = computed(() => {
  // Use runtime config or fallback to process.env
  try {
    return process.env.NODE_ENV === 'development'
  } catch {
    return false
  }
})

// Enhanced error logging for development
onMounted(() => {
  if (isDevelopment.value) {
    console.error('[Error Page] Full error object:', props.error)
    console.error('[Error Page] Error stack:', props.error?.stack)
    console.error('[Error Page] Error data:', props.error?.data)
  }
})

// Error type detection
const getErrorTitle = (): string => {
  const statusCode = props.error?.statusCode

  switch (statusCode) {
    case 404:
      return '页面未找到'
    case 403:
      return '访问被拒绝'
    case 500:
      return '服务器错误'
    case 502:
    case 503:
      return '服务暂时不可用'
    default:
      return '出现了一些问题'
  }
}

const getErrorMessage = (): string => {
  const statusCode = props.error?.statusCode
  const message = props.error?.statusMessage || props.error?.message

  switch (statusCode) {
    case 404:
      return '抱歉，您访问的页面不存在或已被移除。'
    case 403:
      return '您没有权限访问此页面。'
    case 500:
      return '服务器遇到了一个错误，请稍后再试。'
    case 502:
    case 503:
      return '服务正在维护中，请稍后再试。'
    default:
      return message || '系统遇到了意外错误，我们正在处理中。'
  }
}

// Action handlers
const handleRefresh = (): void => {
  if (process.client) {
    window.location.reload()
  }
}

const handleGoHome = (): void => {
  if (process.client) {
    window.location.href = '/'
  }
}

// SEO for error pages
useSeoMeta({
  title: `${getErrorTitle()} | 北美好医生`,
  description: getErrorMessage(),
  robots: 'noindex, nofollow'
})
</script>
